import { useMemo } from "react";
import { useNavigate } from "react-router";
import { clsx } from "clsx";
import { useExpenses } from "../../../lib/expenses/useExpenses";
import { useVendors } from "../../../lib/vendors/useVendors";
import { VendorLogo } from "../../components/VendorLogo";
import type { Vendor } from "../../../lib/vendors/types";
import {
  Card,
  EmptyState,
  formatCents,
  withinRange,
  computeRange,
  type RangePreset,
} from "./shared";

// VendorConcentration — what % of total expense spend lands with the top 5
// vendors. A classic "concentration risk" metric: high values mean the org
// is dependent on a handful of counterparties, which is brittle if one
// vendor lapses, raises prices, or becomes unavailable.
//
// Colour buckets:
//   <40%  green — healthy diversification
//   40-70% amber — moderate
//   >70%  red — concentrated

interface Row {
  id: string | null;
  vendor: Vendor | null;
  displayName: string;
  amountCents: number;
}

function classifyPct(pct: number): { color: string; label: string } {
  if (pct < 40) {
    return { color: "text-emerald-600", label: "Healthy diversification" };
  }
  if (pct <= 70) {
    return { color: "text-amber-600", label: "Moderate concentration" };
  }
  return { color: "text-rose-600", label: "Concentrated — review risk" };
}

export function VendorConcentration({
  preset,
  submittedBy,
}: {
  preset: RangePreset;
  submittedBy?: string | null;
}) {
  const { expenses } = useExpenses();
  const { vendors } = useVendors();
  const navigate = useNavigate();

  const { top, total } = useMemo(() => {
    const { startIso, endIso } = computeRange(preset);
    const byId = new Map<string, number>();
    const byName = new Map<string, number>();
    for (const e of expenses) {
      if (submittedBy && e.submittedBy !== submittedBy) continue;
      if (!withinRange(e.occurredOn, startIso, endIso)) continue;
      if (e.vendorId) {
        byId.set(e.vendorId, (byId.get(e.vendorId) ?? 0) + e.amountCents);
      } else {
        const key = e.vendor?.trim() || "Unknown";
        byName.set(key, (byName.get(key) ?? 0) + e.amountCents);
      }
    }
    const vendorIndex = new Map(vendors.map((v) => [v.id, v]));
    const rows: Row[] = [];
    for (const [id, value] of byId.entries()) {
      const v = vendorIndex.get(id) ?? null;
      rows.push({
        id,
        vendor: v,
        displayName: v?.canonicalName ?? "Unknown",
        amountCents: value,
      });
    }
    for (const [name, value] of byName.entries()) {
      rows.push({ id: null, vendor: null, displayName: name, amountCents: value });
    }
    rows.sort((a, b) => b.amountCents - a.amountCents);
    const totalCents = rows.reduce((acc, r) => acc + r.amountCents, 0);
    return { top: rows.slice(0, 5), total: totalCents };
  }, [expenses, vendors, preset, submittedBy]);

  const topTotal = top.reduce((acc, r) => acc + r.amountCents, 0);
  const pct = total > 0 ? (topTotal / total) * 100 : 0;
  const { color, label } = classifyPct(pct);

  return (
    <Card
      title="Vendor concentration"
      testId="report-vendor-concentration"
    >
      {top.length === 0 || total === 0 ? (
        <EmptyState message="No vendor spend in this window yet." />
      ) : (
        <div className="space-y-3">
          <div>
            <div
              className={clsx("text-3xl font-semibold tabular-nums", color)}
              data-testid="vendor-concentration-pct"
            >
              {pct.toFixed(1)}%
            </div>
            <div className="text-xs text-slate-500 mt-0.5">
              {label} · top 5 of {formatCents(total)}
            </div>
          </div>

          <ul className="divide-y divide-slate-100">
            {top.map((row) => {
              const rowPct = total > 0 ? (row.amountCents / total) * 100 : 0;
              const clickable = row.id != null;
              return (
                <li key={(row.id ?? "name:") + row.displayName}>
                  <button
                    type="button"
                    disabled={!clickable}
                    onClick={() => {
                      if (row.id) navigate(`/vendors/${row.id}`);
                    }}
                    className={clsx(
                      "w-full flex items-center gap-2 py-2 text-left",
                      clickable && "hover:bg-slate-50 -mx-2 px-2 rounded cursor-pointer",
                    )}
                  >
                    {row.vendor ? (
                      <VendorLogo vendor={row.vendor} size={20} />
                    ) : (
                      <span
                        aria-hidden="true"
                        className="inline-flex items-center justify-center w-5 h-5 rounded bg-slate-300 text-white text-[10px] font-semibold"
                      >
                        {row.displayName.charAt(0).toUpperCase() || "?"}
                      </span>
                    )}
                    <span className="text-sm text-slate-700 flex-1 truncate">
                      {row.displayName}
                    </span>
                    <span className="text-xs text-slate-500 tabular-nums">
                      {rowPct.toFixed(1)}%
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </div>
      )}
    </Card>
  );
}
